import { QuoteItemDTO, QuoteProductDTO } from 'pages/quotes/types';

export const updateQuoteItems = (
  items: any[],
  rfqItemId: string,
  props: Record<string, any>
): any[] =>
  // Guard against an absent items list: the Quotes V2 product-match reuse
  // drives this through the v1 `useQuoteStore`, whose `quote.items` is
  // undefined (V2 keeps items in a separate cache), so a swap would otherwise
  // throw on `.map`. The real V2 persistence happens via the item-update API
  // + V2 cache refresh, so returning [] here is safe.
  (items || []).map(item => {
    const isTargetItem = item.rfqItemId === rfqItemId;

    return {
      ...item,
      ...(isTargetItem ? props : {}),
      children: item.children
        ? updateQuoteItems(item.children, rfqItemId, props)
        : []
    };
  });

export const recursiveItemFlatten = (items: any[]): any[] =>
  items.flatMap(i => {
    if (i.type === 'section') {
      return recursiveItemFlatten(i.children);
    }

    if (i.children?.length) {
      return [i, ...recursiveItemFlatten(i.children)];
    }

    return i;
  });

export const updateItemsLoadingState = (
  list: any[],
  items: any[]
): QuoteItemDTO[] =>
  items.map(item => {
    if (item.type === 'section' && Array.isArray(item.children)) {
      return {
        ...item,
        children: updateItemsLoadingState(list, item.children)
      };
    }

    if (item.children?.length) {
      return {
        ...item,
        isPricingFetching:
          list.some(i => i.id === item.id) || item.isPricingFetching,
        children: updateItemsLoadingState(list, item.children)
      };
    }

    return {
      ...item,
      isPricingFetching:
        list.some(i => i.id === item.id) || item.isPricingFetching
    };
  });

export const updateItemsWithPrices = (
  prices: any[],
  items: any[]
): QuoteItemDTO[] =>
  items.map(item => {
    if (item.type === 'section' && Array.isArray(item.children)) {
      return {
        ...item,
        children: updateItemsWithPrices(prices, item.children)
      };
    }

    const priceInfo = prices?.find(p => {
      if (p.id && item.id) {
        return p.id === item.id;
      }

      return p.sku === item.sku;
    });

    if (!priceInfo && item.children?.length === 0) return item;

    const newSell = priceInfo?.price ?? item.sell;
    const newCost = priceInfo?.cost ?? item.cost;

    if (item.children?.length) {
      return {
        ...item,
        isEditable: priceInfo?.isEditable || item.isEditable,
        isPricingFetching: false,
        sell: newSell,
        cost: newCost,
        totalGrossProfit: (newSell - newCost).toFixed(2),
        totalGrossProfitPercentage: (newSell === 0
          ? 0
          : ((newSell - newCost) / newSell) * 100
        ).toFixed(2),
        children: updateItemsWithPrices(prices, item.children)
      };
    }

    return {
      ...item,
      isEditable: priceInfo?.isEditable || item.isEditable,
      isPricingFetching: false,
      sell: newSell,
      cost: newCost,
      totalGrossProfit: (newSell - newCost).toFixed(2),
      totalGrossProfitPercentage: (newSell === 0
        ? 0
        : ((newSell - newCost) / newSell) * 100
      ).toFixed(2)
    };
  });

export const updateItemIds = (localItems: any[], responseItems: any[]): any[] =>
  localItems.map((item, index) => {
    const responseItem = responseItems?.[index];
    if (!responseItem) return item;

    return {
      ...item,
      ...(responseItem.id && { id: responseItem.id }),
      children:
        item.children?.length && responseItem.children?.length
          ? updateItemIds(item.children, responseItem.children)
          : item.children || []
    };
  });

export const updateItemPricing = ({
  id,
  list,
  updates
}: {
  list: any[];
  id: string;
  updates: Partial<QuoteProductDTO>;
}): any[] =>
  list?.map(item => ({
    ...item,
    ...((item.id ?? item._id) === id ? updates : {}),
    children: item.children
      ? updateItemPricing({ list: item.children, id, updates })
      : []
  }));
